import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeName = 'midnight' | 'ocean' | 'forest' | 'rose' | 'pearl' | 'linen' | 'google';

export type ThemeTokens = {
  isLight: boolean;
  app: {
    background: string;
    text: string;
    muted: string;
    border: string;
    surface: string;
    surfaceAlt: string;
    input: string;
    inputBorder: string;
    primary: string;
    primaryText: string;
    activeBg: string;
    activeText: string;
    subtle: string;
    shadow: string;
  };
  nav: {
    background: string;
    text: string;
    muted: string;
    activeBg: string;
    activeText: string;
    border: string;
    panel: string;
    control: string;
  };
  calendar: {
    frame: string;
    panel: string;
    header: string;
    grid: string;
    gridLine: string;
    divider: string;
    text: string;
    muted: string;
    todayBg: string;
    todayText: string;
    contactBg: string;
    allDayBg: string;
    allDayBorder: string;
  };
};

export const THEME_STORAGE_KEY = 'dentec:theme';

export const themeOptions: Array<{ name: ThemeName; label: string; colors: string[] }> = [
  { name: 'midnight', label: 'Midnight', colors: ['#0f172a', '#1e293b', '#38bdf8'] },
  { name: 'ocean', label: 'Ocean', colors: ['#0f172a', '#0e7490', '#67e8f9'] },
  { name: 'forest', label: 'Forest', colors: ['#052e16', '#166534', '#86efac'] },
  { name: 'rose', label: 'Rose', colors: ['#3b0764', '#be123c', '#f9a8d4'] },
  { name: 'pearl', label: 'Pearl', colors: ['#f8fafc', '#dbeafe', '#38bdf8'] },
  { name: 'linen', label: 'Linen', colors: ['#fff7ed', '#fed7aa', '#fb923c'] },
  { name: 'google', label: 'Google', colors: ['#0f172a', '#667eea', '#4285f4', '#34a853', '#fbbc05', '#ea4335'] }
];

const buildTokens = (parts: {
  isLight?: boolean;
  background: string;
  text: string;
  muted: string;
  border: string;
  surface: string;
  surfaceAlt: string;
  input: string;
  inputBorder: string;
  primary: string;
  primaryText?: string;
  activeBg: string;
  activeText: string;
  subtle: string;
  shadow: string;
}): ThemeTokens => ({
  isLight: parts.isLight ?? false,
  app: {
    background: parts.background,
    text: parts.text,
    muted: parts.muted,
    border: parts.border,
    surface: parts.surface,
    surfaceAlt: parts.surfaceAlt,
    input: parts.input,
    inputBorder: parts.inputBorder,
    primary: parts.primary,
    primaryText: parts.primaryText ?? '#ffffff',
    activeBg: parts.activeBg,
    activeText: parts.activeText,
    subtle: parts.subtle,
    shadow: parts.shadow
  },
  nav: {
    background: parts.surface,
    text: parts.text,
    muted: parts.muted,
    activeBg: parts.activeBg,
    activeText: parts.activeText,
    border: parts.border,
    panel: parts.subtle,
    control: parts.input
  },
  calendar: {
    frame: parts.border,
    panel: parts.surface,
    header: parts.surface,
    grid: parts.background,
    gridLine: parts.isLight ? '#e5e7eb' : '#1e293b',
    divider: parts.border,
    text: parts.text,
    muted: parts.muted,
    todayBg: parts.activeBg,
    todayText: parts.activeText,
    contactBg: parts.primary,
    allDayBg: parts.subtle,
    allDayBorder: parts.border
  }
});

export const themeTokens: Record<ThemeName, ThemeTokens> = {
  midnight: buildTokens({
    background: '#0f172a',
    text: '#f1f5f9',
    muted: '#94a3b8',
    border: '#334155',
    surface: '#1e293b',
    surfaceAlt: '#334155',
    input: '#0f172a',
    inputBorder: '#334155',
    primary: '#667eea',
    activeBg: '#1e293b',
    activeText: '#ffffff',
    subtle: '#0a0f1a',
    shadow: '0 1px 3px rgba(0,0,0,0.3)'
  }),
  ocean: buildTokens({
    background: '#082f49',
    text: '#ecfeff',
    muted: '#a5f3fc',
    border: '#0e7490',
    surface: '#164e63',
    surfaceAlt: '#155e75',
    input: '#083344',
    inputBorder: '#0e7490',
    primary: '#06b6d4',
    activeBg: '#0e7490',
    activeText: '#ffffff',
    subtle: '#0c4a6e',
    shadow: '0 1px 3px rgba(0,0,0,0.28)'
  }),
  forest: buildTokens({
    background: '#052e16',
    text: '#f0fdf4',
    muted: '#bbf7d0',
    border: '#166534',
    surface: '#14532d',
    surfaceAlt: '#166534',
    input: '#052e16',
    inputBorder: '#166534',
    primary: '#22c55e',
    activeBg: '#166534',
    activeText: '#ffffff',
    subtle: '#064e3b',
    shadow: '0 1px 3px rgba(0,0,0,0.28)'
  }),
  rose: buildTokens({
    background: '#3b0764',
    text: '#fdf2f8',
    muted: '#f9a8d4',
    border: '#9d174d',
    surface: '#581c87',
    surfaceAlt: '#831843',
    input: '#3b0764',
    inputBorder: '#9d174d',
    primary: '#e11d48',
    activeBg: '#be123c',
    activeText: '#ffffff',
    subtle: '#4c0519',
    shadow: '0 1px 3px rgba(0,0,0,0.28)'
  }),
  pearl: buildTokens({
    isLight: true,
    background: '#f8fafc',
    text: '#0f172a',
    muted: '#64748b',
    border: '#cbd5e1',
    surface: '#ffffff',
    surfaceAlt: '#e2e8f0',
    input: '#ffffff',
    inputBorder: '#cbd5e1',
    primary: '#2563eb',
    activeBg: '#dbeafe',
    activeText: '#1d4ed8',
    subtle: '#f1f5f9',
    shadow: '0 1px 3px rgba(15,23,42,0.15)'
  }),
  linen: buildTokens({
    isLight: true,
    background: '#fff7ed',
    text: '#1f2937',
    muted: '#78716c',
    border: '#fed7aa',
    surface: '#fffbeb',
    surfaceAlt: '#fed7aa',
    input: '#fffaf0',
    inputBorder: '#fdba74',
    primary: '#ea580c',
    activeBg: '#ffedd5',
    activeText: '#c2410c',
    subtle: '#fff1db',
    shadow: '0 1px 3px rgba(124,45,18,0.14)'
  }),
  google: buildTokens({
    isLight: true,
    background: '#f8fafd',
    text: '#3c4043',
    muted: '#5f6368',
    border: '#dadce0',
    surface: '#ffffff',
    surfaceAlt: '#f1f3f4',
    input: '#ffffff',
    inputBorder: '#dadce0',
    primary: '#4285f4',
    activeBg: '#e8f0fe',
    activeText: '#1967d2',
    subtle: '#f1f3f4',
    shadow: '0 1px 2px rgba(60,64,67,0.15)'
  })
};

type ThemeContextValue = {
  currentTheme: ThemeName;
  setCurrentTheme: (theme: ThemeName) => void;
  tokens: ThemeTokens;
  activeTheme: { name: ThemeName; label: string; colors: string[] };
};

const isValidTheme = (value: string | null): value is ThemeName => {
  return value !== null && Object.prototype.hasOwnProperty.call(themeTokens, value);
};

const getStoredTheme = (): ThemeName => {
  if (typeof window === 'undefined') return 'midnight';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isValidTheme(stored) ? stored : 'midnight';
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const applyCssVariables = (tokens: ThemeTokens, theme: ThemeName) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.setProperty('--app-bg', tokens.app.background);
  root.style.setProperty('--app-text', tokens.app.text);
  root.style.setProperty('--app-muted', tokens.app.muted);
  root.style.setProperty('--app-border', tokens.app.border);
  root.style.setProperty('--app-surface', tokens.app.surface);
  root.style.setProperty('--app-surface-alt', tokens.app.surfaceAlt);
  root.style.setProperty('--app-input', tokens.app.input);
  root.style.setProperty('--app-primary', tokens.app.primary);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>(getStoredTheme);
  const tokens = themeTokens[currentTheme];
  const activeTheme = themeOptions.find(theme => theme.name === currentTheme) || themeOptions[0];

  useEffect(() => {
    window.localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
    applyCssVariables(tokens, currentTheme);
  }, [currentTheme, tokens]);

  const value = useMemo<ThemeContextValue>(() => ({
    currentTheme,
    setCurrentTheme,
    tokens,
    activeTheme
  }), [activeTheme, currentTheme, tokens]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const value = useContext(ThemeContext);
  if (!value) {
    return {
      currentTheme: 'midnight' as ThemeName,
      setCurrentTheme: () => undefined,
      tokens: themeTokens.midnight,
      activeTheme: themeOptions[0]
    };
  }
  return value;
};
